import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Search, Trophy, XCircle, Shuffle, AlertTriangle, Trash2 } from "lucide-react";
import { LeadBadge, LEAD_ORDER, LEAD_STYLE, fmtDate } from "@/components/bp/bpLabels";

export const BpLeadsTab = ({ partners, refreshKey, onChanged }) => {
  const [data, setData] = useState({ items: [], by_status: {} });
  const [status, setStatus] = useState("all");
  const [bpId, setBpId] = useState("all");
  const [q, setQ] = useState("");
  const [action, setAction] = useState(null); // {lead, type: won|lost|reassign}
  const [message, setMessage] = useState("");
  const [notify, setNotify] = useState(true);
  const [target, setTarget] = useState("");
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    try { const { data: d } = await api.get("/admin/bp/leads", { params: { status, bp_id: bpId, q: q || undefined } }); setData(d); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status, bpId, refreshKey]);

  const openAction = async (lead, type) => {
    setAction({ lead, type }); setMessage(""); setNotify(true); setTarget(""); setDetail(null);
    try { const { data: d } = await api.get(`/admin/bp/leads/${lead.id}`); setDetail(d); } catch { /* silent */ }
  };
  const run = async () => {
    setBusy(true);
    try {
      if (action.type === "reassign") {
        if (!target) { toast.error("Choisissez un partenaire"); return; }
        await api.post(`/admin/bp/leads/${action.lead.id}/reassign`, { bp_id: target, message });
        toast.success("Lead réattribué — les deux partenaires sont prévenus");
      } else {
        const { data: r } = await api.post(`/admin/bp/leads/${action.lead.id}/status`, { status: action.type, message, notify });
        toast.success(`Lead ${LEAD_STYLE[action.type].label.toLowerCase()}${r.notified ? " — partenaire prévenu" : ""}`);
      }
      setAction(null);
      await Promise.all([load(), onChanged?.()]);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const removeLead = async (lead) => {
    if (!window.confirm(`Supprimer définitivement le lead ${lead.reference} (${lead.company_name}) ?`)) return;
    try { await api.delete(`/admin/bp/leads/${lead.id}`); toast.success("Lead supprimé"); await Promise.all([load(), onChanged?.()]); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="bg-white border border-[#DEE2E6]" data-testid="bp-leads-tab">
      <header className="p-4 border-b border-[#DEE2E6] flex flex-wrap items-center gap-2">
        <div className="flex flex-wrap gap-1.5">
          {[["all", "Tous"], ...LEAD_ORDER.map((k) => [k, `${LEAD_STYLE[k].label} · ${data.by_status?.[k] || 0}`])].map(([k, l]) => (
            <button key={k} type="button" onClick={() => setStatus(k)} data-testid={`bp-leads-filter-${k}`}
                    className={`text-[10px] uppercase tracking-wider font-bold px-2.5 py-1.5 border transition ${status === k ? "bg-[#002FA7] text-white border-[#002FA7]" : "bg-white border-[#DEE2E6] hover:border-[#002FA7]"}`}>{l}</button>
          ))}
        </div>
        <select value={bpId} onChange={(e) => setBpId(e.target.value)} data-testid="bp-leads-filter-bp" className="h-8 border border-[#DEE2E6] bg-white px-2 text-xs">
          <option value="all">Tous les partenaires</option>
          {partners.map((p) => <option key={p.id} value={p.id}>{`${p.code} — ${p.prenom} ${p.nom}`}</option>)}
        </select>
        <form className="flex items-center gap-1 ml-auto" onSubmit={(e) => { e.preventDefault(); load(); }}>
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Société, contact, SIRET, référence…" className="h-8 w-56 text-xs" data-testid="bp-leads-search" />
          <Button type="submit" size="sm" variant="outline" className="h-8" data-testid="bp-leads-search-btn"><Search className="h-3.5 w-3.5" /></Button>
        </form>
      </header>
      {data.items.length === 0 ? <div className="p-10 text-center text-sm text-[#868E96]" data-testid="bp-leads-empty">Aucun lead.</div> : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="bg-[#F8F9FA] text-left text-[10px] uppercase tracking-wider"><tr><th className="p-3">Réf.</th><th className="p-3">Prospect</th><th className="p-3">Contact</th><th className="p-3">Partenaire</th><th className="p-3">Date</th><th className="p-3">Statut</th><th className="p-3" /></tr></thead>
            <tbody>
              {data.items.map((l) => (
                <tr key={l.id} className="border-t border-[#F8F9FA] align-top" data-testid={`bp-lead-row-${l.id}`}>
                  <td className="p-3 font-mono text-[10px]">{l.reference}</td>
                  <td className="p-3"><div className="font-semibold">{l.company_name}</div><div className="text-[10px] text-[#868E96]">{[l.ville, l.secteur].filter(Boolean).join(" · ")}{l.siret ? ` · ${l.siret}` : ""}</div></td>
                  <td className="p-3">{l.contact_prenom} {l.contact_nom}<div className="text-[10px] text-[#868E96]">{l.email}{l.telephone ? ` · ${l.telephone}` : ""}</div></td>
                  <td className="p-3"><span className="font-mono text-[10px] text-[#002FA7] font-bold">{l.bp_code}</span><div className="text-[10px]">{l.bp_name}</div></td>
                  <td className="p-3 whitespace-nowrap">{fmtDate(l.created_at)}</td>
                  <td className="p-3"><LeadBadge status={l.status} testid={`bp-lead-status-${l.id}`} /></td>
                  <td className="p-3 text-right whitespace-nowrap">
                    {!["won", "lost"].includes(l.status) && <>
                      <Button size="sm" onClick={() => openAction(l, "won")} className="h-7 text-[10px] uppercase bg-[#059669] text-white mr-1" data-testid={`bp-lead-won-${l.id}`}><Trophy className="h-3 w-3 mr-1" />Gagné</Button>
                      <Button size="sm" variant="outline" onClick={() => openAction(l, "lost")} className="h-7 text-[10px] uppercase mr-1" data-testid={`bp-lead-lost-${l.id}`}><XCircle className="h-3 w-3 mr-1" />Perdu</Button>
                    </>}
                    <Button size="sm" variant="outline" onClick={() => openAction(l, "reassign")} className="h-7 text-[10px] uppercase mr-1" data-testid={`bp-lead-reassign-${l.id}`}><Shuffle className="h-3 w-3 mr-1" />Réattribuer</Button>
                    <Button size="sm" variant="ghost" onClick={() => removeLead(l)} className="h-7 w-7 p-0 text-[#868E96] hover:text-[#E60000]" title="Supprimer le lead" data-testid={`bp-lead-delete-${l.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={!!action} onOpenChange={(o) => { if (!o) setAction(null); }}>
        <DialogContent className="max-w-lg" data-testid="bp-lead-action-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl font-black">{action?.type === "reassign" ? "Réattribuer le lead" : action?.type === "won" ? "Marquer gagné" : "Marquer perdu"}</DialogTitle>
            <DialogDescription className="text-xs">{action?.lead.reference} · {action?.lead.company_name} · actuellement {action?.lead.bp_code}</DialogDescription>
          </DialogHeader>
          {detail?.duplicates && (detail.duplicates.blocking.length > 0 || detail.duplicates.warnings.length > 0) && (
            <div className="border border-[#FFD700] bg-[#FFFBEB] p-2 text-[11px] space-y-1" data-testid="bp-lead-dup-info">
              {[...detail.duplicates.blocking, ...detail.duplicates.warnings].map((w) => <div key={w} className="flex items-start gap-1.5 text-[#92400E]"><AlertTriangle className="h-3.5 w-3.5 shrink-0" />{w}</div>)}
            </div>
          )}
          {action?.type === "reassign" && (
            <select value={target} onChange={(e) => setTarget(e.target.value)} data-testid="bp-lead-reassign-target" className="h-10 w-full border border-[#DEE2E6] bg-white px-2 text-sm">
              <option value="">Choisir le nouveau partenaire…</option>
              {partners.filter((p) => p.id !== action.lead.bp_id).map((p) => <option key={p.id} value={p.id}>{`${p.code} — ${p.prenom} ${p.nom}${p.zone ? ` (${p.zone})` : ""}`}</option>)}
            </select>
          )}
          <Textarea value={message} onChange={(e) => setMessage(e.target.value)} rows={3} placeholder="Message transmis au partenaire (facultatif)" className="text-xs" data-testid="bp-lead-action-message" />
          {action?.type !== "reassign" && <label className="flex items-center gap-2 text-xs cursor-pointer"><Checkbox checked={notify} onCheckedChange={(v) => setNotify(!!v)} data-testid="bp-lead-action-notify" />Prévenir le partenaire (email + push)</label>}
          <div className="flex justify-end gap-2"><Button variant="outline" onClick={() => setAction(null)}>Annuler</Button><Button onClick={run} disabled={busy} className="bg-[#0A0A0A] text-white" data-testid="bp-lead-action-confirm">{busy ? "…" : "Confirmer"}</Button></div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
